import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../lib/api';
import { AdminOrganization } from '../../../types/admin';
import { MarginQuery, MarginRollup, MarginSort, MarginTotals } from '../../../types/agentUsage';
import { useToastContext } from '../../../contexts/ToastContext';
import { ERROR_MESSAGES } from '../../../constants/error-messages';
import { TABLE_TD as TD, TABLE_TH as TH, formatCost, formatRatio } from '../../../utils/agentUsageUtils';
import { Spinner, WindowToggle } from '../../ui';

/** Admin margin per campaign, sorted and paged by the server. */

const PAGE_SIZE = 50;
const COLUMNS: [MarginSort, string][] = [
  ['credits_charged', 'Credits charged'],
  ['credits_exempt', 'Would-be credits (exempt)'],
  ['paid_revenue_usd', 'Paid revenue'],
  ['would_be_revenue_usd', 'Would-be revenue'],
  ['model_cost_usd', 'Model cost'],
  ['vendor_cost_usd', 'Vendor cost'],
  ['margin_usd', 'Margin if billed'],
  ['margin_ratio', 'Ratio if billed'],
];

const cell = (key: MarginSort, t: MarginTotals): string => {
  if (key === 'margin_ratio') return formatRatio(t.margin_ratio);
  if (key === 'credits_charged' || key === 'credits_exempt') return t[key].toLocaleString();
  return formatCost(t[key]);
};

const Cells: React.FC<{ t: MarginTotals }> = ({ t }) => (
  <>
    {COLUMNS.map(([key]) => (
      <td key={key} className={`${TD} tabular-nums`}>
        {cell(key, t)}
      </td>
    ))}
  </>
);

export const MarginTab: React.FC = () => {
  const { showToast } = useToastContext();
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [query, setQuery] = useState<MarginQuery>({
    days: 30,
    workspaceId: null,
    sort: 'margin_usd',
    desc: true,
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [rollup, setRollup] = useState<MarginRollup | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    apiClient
      .getAdminOrganizations()
      .then(setOrganizations)
      .catch((error) => console.error('Failed to load workspaces for the margin filter:', error));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    apiClient
      .getAdminMargin(query)
      .then((res) => {
        if (cancelled) return;
        setRollup(res);
        setState('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load margin rollup:', error);
        showToast(ERROR_MESSAGES.MARGIN_LOAD_FAILED, 'error');
        setState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [query, showToast]);

  const update = (change: Partial<MarginQuery>) => setQuery((q) => ({ ...q, offset: 0, ...change }));
  const sortBy = (sort: MarginSort) => update({ sort, desc: query.sort === sort ? !query.desc : true });
  const page = (offset: number) => setQuery((q) => ({ ...q, offset }));

  return (
    <section id="margin-tab" aria-labelledby="margin-heading">
      <div className="flex items-center justify-between mb-4 gap-4">
        <h3 id="margin-heading" className="text-base font-semibold text-neutral-900">
          Margin by campaign
        </h3>
        <div className="flex items-center gap-3">
          <label htmlFor="margin-workspace" className="text-sm text-neutral-600">
            Workspace
          </label>
          <select
            id="margin-workspace"
            value={query.workspaceId ?? ''}
            onChange={(e) => update({ workspaceId: e.target.value || null })}
            className="text-sm border border-neutral-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-600"
          >
            <option value="">All workspaces</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name ?? org.domain ?? org.id}
              </option>
            ))}
          </select>
          <WindowToggle days={query.days} onChange={(days) => update({ days })} />
        </div>
      </div>

      {state === 'loading' && <Spinner size="md" text="Loading margin..." center />}
      {state === 'failed' && (
        <p className="text-sm text-error-600 py-8 text-center">
          Margin could not be loaded.{' '}
          <button type="button" onClick={() => setQuery((q) => ({ ...q }))} className="underline">
            Try again
          </button>
        </p>
      )}
      {state === 'ready' && rollup && (
        <>
          <div className="overflow-x-auto border border-neutral-200 rounded-md bg-white">
            <table className="min-w-full" aria-label="Margin by campaign">
              <thead className="bg-neutral-50">
                <tr>
                  <th scope="col" className={TH}>Workspace</th>
                  <th scope="col" className={TH}>Campaign</th>
                  {COLUMNS.map(([key, label]) => (
                    <th key={key} scope="col" className={TH} aria-sort={query.sort === key ? (query.desc ? 'descending' : 'ascending') : undefined}>
                      <button type="button" onClick={() => sortBy(key)} className="uppercase hover:text-primary-700">
                        {label}
                        {query.sort === key ? (query.desc ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {rollup.rows.map((row) => (
                  <tr key={`${row.workspace_id}:${row.gtm_project_id ?? 'none'}`}>
                    <td className={TD}>{row.workspace_name ?? row.workspace_id}</td>
                    <td className={TD}>{row.gtm_project_id ? (row.project_name ?? row.gtm_project_id) : '(no campaign)'}</td>
                    <Cells t={row} />
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-neutral-50 border-t border-neutral-200 font-semibold">
                <tr>
                  <td className={TD} colSpan={2}>
                    Total ({rollup.total})
                  </td>
                  <Cells t={rollup.totals} />
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex justify-end gap-3 mt-3 text-sm">
            <button type="button" disabled={query.offset === 0} onClick={() => page(query.offset - PAGE_SIZE)} className="disabled:opacity-40">
              Previous
            </button>
            <button type="button" disabled={query.offset + PAGE_SIZE >= rollup.total} onClick={() => page(query.offset + PAGE_SIZE)} className="disabled:opacity-40">
              Next
            </button>
          </div>
        </>
      )}
    </section>
  );
};
